import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { ApplicationConfigurationService } from './../application-configuration/application-configuration.service';
import { SurvivalHorizonService } from './../survival-horizon/survival-horizon.service';
import { AppConfig } from './../app.config';
import { SnapshotService } from './../snapshot/snapshot.service';
import { Observable, Subject, Observer, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { PollingStatus } from './polling-status';
import { Injectable } from '@angular/core';
import { PollableService } from './pollable-service';
import { RegistryService } from './registry-service';
import { TenantInfo } from './../tenant/tenant-info';
import { InitializationStep } from '../snapshot/initialization-step';
import { ApplicationConfiguration } from '@liquitics/transaction';
import * as moment from 'moment';


@Injectable()
export class ServiceRegistry {
    private services: RegistryService[] = [];
    private pollableServices: PollableService[] = [];
    private pollingStarted = false;

    private pollingProblemCounter = -1;
    pollingStatus: Subject<string>;
    tenantInfo: TenantInfo;

    private requiredSteps: number;
    private completedSteps: number;

    private serviceStartupTime: moment.Moment;

    constructor(private snapshotService: SnapshotService,
        private appConfig: AppConfig,
        private dashboardEventService: DashboardEventService) {
        this.pollingStatus = new Subject();
    }

    setTenantInfo(tenantInfo: TenantInfo) {
        this.tenantInfo = tenantInfo;
        this.services.forEach(s => {
            s.setTenantInfo(tenantInfo);
        });
    }

    registerService(registryService: RegistryService) {
        this.services.push(registryService);
        registryService.setTenantInfo(this.tenantInfo);
        if (this.instanceOfPollableService(registryService)) {
            this.pollableServices.push(registryService);
            if (this.pollingStarted) {
                (<PollableService>registryService).startPolling();
            }
        }
    }

    // private pushConnectionUnstableError() {
    //     this.dashboardEventService.emergencyMessageSubject.next({
    //         severity: 'warn',
    //         summary: 'Connection instability',
    //         detail: 'Errors detected attempting to reach servers'
    //     });
    // }
    // private pushConnectionDisconnectError() {
    //     this.dashboardEventService.emergencyMessageSubject.next({
    //         severity: 'error',
    //         summary: 'Disconnected',
    //         detail: 'Unable to reach servers'
    //     });
    // }

    startServiceMonitor() {
        this.serviceStartupTime = moment.utc();
        // After 20 seconds, every 30 seconds...
        timer(20000, 30000)
            .subscribe({
                next: () => {
                    const now = moment.utc();
                    this.pollableServices.forEach((pollableService: PollableService) => {
                        if (!pollableService.isPollingDataPaused()) {
                            const last = pollableService.lastPolledTime();
                            if (last) {
                                const secondsSinceLast = now.diff(last, 'seconds');
                                if (secondsSinceLast > 60) {
                                    this.pollingProblemOccurred();
                                }
                            }
                        }
                    })
                }
            });
    }

    async startUpServices(): Promise<InitializationStep[]> {
        const appConfigService: ApplicationConfigurationService = this.getApplicationConfigurationService();
        this.startServiceMonitor();
        console.log('Starting up services...');
        let todayFolder = this.appConfig.getTodayStartUTC().format('YYYY/MM/DD');
        let todayUpdated = false;
        try {
            await this.snapshotService.checkIfSnapshotFilesAvailable(todayFolder);
        } catch (err) {
            // Not all files available, let's try moving the date back
            this.appConfig.setTodayStartUTC(this.appConfig.getTodayStartUTC().subtract(1, 'day'));
            todayUpdated = true;
            todayFolder = this.appConfig.getTodayStartUTC().format('YYYY/MM/DD');
            console.log('Today\'s EOD positions are not yet available. Attempting to switch to yesterday\'s date.');
            await this.snapshotService.checkIfSnapshotFilesAvailable(todayFolder);
        }
        let applicationConfiguration: ApplicationConfiguration;
        try {
            applicationConfiguration = await appConfigService.getApplicationConfiguration(this.appConfig.getTodayString()).pipe(take(1)).toPromise();
            this.snapshotService.applicationConfiguration = applicationConfiguration;
            this.dashboardEventService.applicationConfigurationSubject.next(applicationConfiguration);
        } catch (err) {
            console.log(err);
            throw new Error('Unable to find Application Configurations for user.');
        }
        if (applicationConfiguration.isMarkedInactive) {
            throw new Error('Your account has been marked inactive. Please contact your administrator.');
        }
        if (!this.appConfig.activeUserProfile) {
            if (applicationConfiguration.userProfiles.length === 0) {
                throw new Error('User does not have any User Profiles. Please contact your administrator.');
            }
            let profileIndex = appConfigService.getLastAccessedUserProfileIndex();
            profileIndex = Math.max(profileIndex, 0);
            profileIndex = Math.min(profileIndex, applicationConfiguration.userProfiles.length - 1);
            this.appConfig.activeUserProfile = applicationConfiguration.userProfiles[profileIndex];
        }
        this.startPollingServices();
        if (todayUpdated) {
            this.services
                .filter((registryService: RegistryService) => {
                    return this.instanceOfSurvivalHorizonService(registryService);
                })
                .forEach((service: SurvivalHorizonService) => {
                    service.initializeWithToday();
                });
        }
        let steps: InitializationStep[] = this.snapshotService.initializationSteps();
        const serviceSteps: InitializationStep[] = this.createServiceSteps();
        steps = steps.concat(serviceSteps);
        return steps;
    }

    reinitializeServices() {
        this.services
            .filter((registryService: RegistryService) => {
                return this.instanceOfSurvivalHorizonService(registryService);
            })
            .forEach((service: SurvivalHorizonService) => {
                service.initializeWithToday();
            });
    }

    private createServiceSteps(): InitializationStep[] {
        const steps: InitializationStep[] = [];
        // const applicationConfigurationsStep = new InitializationStep('ApplicationConfigurations');
        // steps.push(applicationConfigurationsStep);
        // applicationConfigurationsStep.promise = this.getApplicationConfigurationService().
        // getApplicationConfiguration(this.appConfig.getTodayString())
        //     .take(1)
        //     .toPromise()
        //     .then((applicationConfiguration: ApplicationConfiguration) => {
        //         this.snapshotService.applicationConfiguration = applicationConfiguration;
        //         this.appConfig.activeUserProfile = applicationConfiguration.userProfiles[0];
        //         return Promise.resolve(true);
        //     })
        //     .catch((err: any) => {
        //         applicationConfigurationsStep.progress.next('Error: ' + err);
        //         return Promise.reject(err);
        //     });
        // ;
        return steps;
    }

    private getApplicationConfigurationService(): ApplicationConfigurationService {
        return this.services
            .filter((registryService: RegistryService) => {
                return this.instanceOfApplicationConfigurationService(registryService);
            })
            .map((registryService: RegistryService) => {
                return registryService as ApplicationConfigurationService;
            })
        [0];
    }

    instanceOfPollableService(object: any): object is PollableService {
        return (<PollableService>object).startPolling !== undefined;
    }

    instanceOfSurvivalHorizonService(object: any): object is SurvivalHorizonService {
        return (<SurvivalHorizonService>object).initializeWithToday !== undefined;
    }

    instanceOfApplicationConfigurationService(object: any): object is ApplicationConfigurationService {
        return (<ApplicationConfigurationService>object).getApplicationConfiguration !== undefined;
    }

    pollingProblemOccurred() {
        this.pollingProblemCounter++;
        if (this.pollingProblemCounter >= PollingStatus.MAX_ERRORS_ALLOWED) {
            this.pollingProblemCounter = PollingStatus.MAX_ERRORS_ALLOWED;
            this.pollingStatus.next(PollingStatus.DISCONNECTED);
            // this.pushConnectionDisconnectError();
        } else if (this.pollingProblemCounter === 1) {
            this.pollingStatus.next(PollingStatus.UNSTABLE);
            // this.pushConnectionUnstableError();
        }
    }

    pollingSuccessful() {
        if (this.pollingProblemCounter === -1) {
            // First time startup
            this.pollingProblemCounter = 0;
            this.pollingStatus.next(PollingStatus.OK);
            return;
        }
        if (this.pollingProblemCounter > 0) {
            this.pollingProblemCounter--;
            if (this.pollingProblemCounter === PollingStatus.MAX_ERRORS_ALLOWED - 1) {
                this.pollingStatus.next(PollingStatus.UNSTABLE);
            } else if (this.pollingProblemCounter === 0) {
                this.pollingStatus.next(PollingStatus.OK);
            }
        }
    }

    startPollingServices() {
        if (this.appConfig.activeUserProfile.usePollingService() && !this.appConfig.asOfDateMode) {
            console.log('Starting Polling Service');
            this.pollableServices.forEach(s => {
                if (this.pollingStarted) {
                    s.resume();
                } else {
                    s.startPolling();
                }
            });
            this.pollingStarted = true;
        }
    }

    pausePollingServices(pollingStatus?: string) {
        console.log('Polling Services Paused');
        if (!pollingStatus) {
            pollingStatus = PollingStatus.PAUSED;
        }
        this.pollingStatus.next(pollingStatus);
        this.pollableServices.forEach(s => {
            s.pause();
        });
    }

    resumePollingServices() {
        if (this.appConfig.activeUserProfile.usePollingService() && !this.appConfig.asOfDateMode) {
            console.log('Polling Services Resumed');
            this.pollingStatus.next(PollingStatus.OK);
            this.pollableServices.forEach(s => {
                s.resume();
            });
        }
    }
}
